import React, { useState, useEffect, useRef } from 'react';
import { isEmail, isEmpty } from 'validator';
import { connect } from 'react-redux';
import { selector } from 'apps/services';
import Rating from 'react-rating';
import { useParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { Footer } from 'components/core';
import { Table, Button, Input } from 'reactstrap';
import { DOWNLOAD_TRANSPARENCY_SOFTWARE_LINK, UNIT_KW, PREFERRED_COUNTRIES, COUNTRIES_EXTN, CREDIT_CARD_TEXT, PSP_TYPES, TOAST_AUTO_CLOSE, TOAST_POSITION, LANGUAGES, ENGLISH_LANG_SHORT, CREDIT_CARD_PAYMENT_TYPE } from 'configs/constants';
import { toast, ToastContainer } from 'react-toastify';
import { attemptSetLanguage } from 'apps/modules/app/AppActions';
import { attemptGetReceipt, attemptUpdateReceipt, attemptResetReceiptNotificationState } from 'apps/modules/receipt/ReceiptActions';
import { PDFExport } from '@progress/kendo-react-pdf';
import { QRCode } from 'react-qrcode-logo';
import params from 'configs/params';
import ReactPhoneInput from 'react-phone-input-2';
import { getText } from 'helpers/app/getText';
import { produce, setAutoFreeze } from 'immer';
import i18n from '../../i18n';
import 'react-phone-input-2/lib/style.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faCancel, faEdit } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment-timezone';

function Main({ app, receipt, attemptGetReceipt, attemptUpdateReceipt, attemptResetReceiptNotificationState, attemptSetLanguage }) {
  const resume = useRef(null);
  setAutoFreeze(false);
  const [isEditCustomer, setIsEditCustomer] = useState(false);

  const [fields, setFields] = useState({
    customerName: '',
    customerEmail: '',
    customerMobilePhone: '',
    customerVatNumber: '',
    taxpayerIdentificationNumber: '',
  });

  const [errors, setErrors] = useState({
    customerEmail: '',
  });

  const { receiptid } = useParams();

  useEffect(() => {
    attemptGetReceipt(receiptid);
  }, []);

  useEffect(() => {
    if (receipt.get('receipt')?._id?.length > 0 && receipt.get('receipt')?.cartSettings?.receiptCountryCode) {
      const receiptCountryCode = receipt.get('receipt').cartSettings.receiptCountryCode.toLowerCase();
      const appLanguage = LANGUAGES.filter((lng) => lng.countryCode === receiptCountryCode);

      if (appLanguage?.length > 0) {
        i18n.changeLanguage(appLanguage[0].locale);
        attemptSetLanguage(appLanguage[0].locale);
      } else {
        i18n.changeLanguage(ENGLISH_LANG_SHORT);
        attemptSetLanguage(ENGLISH_LANG_SHORT);
      }
    }
  }, [receipt.get('receipt')]);

  useEffect(() => {
    if (receipt.get('isReceiptUpdated')) {
      toast.dismiss();
      toast.success(getText('notifications:RECEIPT_UPDATED_MSG'), { autoClose: 3000 });
      attemptResetReceiptNotificationState('isReceiptUpdated');
      setIsEditCustomer(false);
      attemptGetReceipt(receiptid);
    }
  }, [receipt.get('isReceiptUpdated')]);

  const onViewAsPDFClicked = () => {
    if (resume.current) {
      resume.current.save();
    }
  };

  const onLanguageChange = (opt) => {
    const appLanguage = LANGUAGES.filter((lng) => lng.value === opt?.value);

    i18n.changeLanguage(appLanguage[0].locale);
    attemptSetLanguage(appLanguage[0].locale);
  };

  const onSetEditCustomer = () => {
    if (!isEditCustomer) {
      const receiptObj = receipt.get('receipt');
      setFields(
        produce((draft) => {
          draft.customerName = receiptObj.customerName;
          draft.customerEmail = receiptObj.customerEmail;
          draft.customerMobilePhone = receiptObj.customerMobilePhone;
          draft.customerVatNumber = receiptObj.customerVatNumber;
        }),
      );
    }
    setIsEditCustomer(!isEditCustomer);
  };

  const validate = (name, value) => {
    switch (name) {
      case 'customerEmail':
        if (value?.length > 0 && !isEmail(value)) {
          return getText('notifications:INVALID_EMAIL_MSG');
        } else {
          return '';
        }
    }
  };

  const onSaveCustomer = () => {
    let errorList = {};

    Object.keys(fields).map((name) => {
      const error = validate(name, fields[name]);
      if (error?.length > 0) {
        errorList[name] = error;
      }
    });

    if (Object.keys(errorList).length > 0) {
      setErrors(errorList);
      toast.dismiss();
      toast.error(getText('notifications:VALIDATION_ERRORS_MSG'), { autoClose: 3000, position: TOAST_POSITION });
      return;
    }

    const receiptObj = receipt.get('receipt');
    attemptUpdateReceipt({
      receiptID: receiptObj._id,
      merchantID: receiptObj.merchantID,
      ...fields,
    });
  };

  const onChange = (ev) => {
    const { name, value } = ev.target;

    setFields(
      produce((draft) => {
        draft[name] = value;
      }),
    );
  };

  const onPhoneChange = (val) => {
    setFields(
      produce((fields) => {
        fields.customerMobilePhone = val;
      }),
    );
  };

  const onDownloadXMLFile = (ev) => {
    ev.preventDefault();
    var xmltext = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><values><value context="Transaction.End"><publicKey>${atob(receiptObj.signedMeterDataPublicKey)}</publicKey><signedData>${atob(receiptObj.signedMeterData)}</signedData></value></values>`;

    var filename = `SignedMeterData_${moment().format('YYYYMMDDHHMMSS')}.xml`;
    var pom = document.createElement('a');
    var bb = new Blob([xmltext], { type: 'text/plain' });

    pom.setAttribute('href', window.URL.createObjectURL(bb));
    pom.setAttribute('download', filename);

    pom.dataset.downloadurl = ['text/plain', pom.download, pom.href].join(':');
    pom.draggable = true;
    pom.classList.add('dragout');

    pom.click();
  };

  const receiptObj = receipt.get('receipt');
  const language = app.get('language');

  const streetRow1 = receiptObj?.merchantAddress?.[0]?.streetRow1 || '';
  const streetRow2 = receiptObj?.merchantAddress?.[0]?.streetRow2 || '';
  const zip = receiptObj?.merchantAddress?.[0]?.zip || '';
  const city = receiptObj?.merchantAddress?.[0]?.city || '';
  const state = receiptObj?.merchantAddress?.[0]?.state || '';
  const country = receiptObj?.merchantAddress?.[0]?.country || '';
  const email = receiptObj?.email || '';
  const phone = receiptObj?.phone || '';

  let totalSeconds = 0;

  if (receiptObj?.usageStartDate?.length > 0 && receiptObj?.usageStopDate?.length > 0) {
    totalSeconds = moment.utc(receiptObj.usageStopDate, true).diff(moment.utc(receiptObj.usageStartDate, true), 'seconds');
  } else {
    totalSeconds = receiptObj.usageTotalTimeMinutes * 60;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return (
    <div className="wrapper">
      <section className="section-container">
        <ToastContainer autoClose={TOAST_AUTO_CLOSE} position={TOAST_POSITION} />
        <div className="text-center">
          <img src={receiptObj.merchantLogoImageURL} className="header-img text-center" />
        </div>
        <PDFExport
          scale={0.6}
          paperSize="A4"
          forcePageBreak=".page-break"
          fileName={`RECEIPT-${new Date().toLocaleString('en-IN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false, // Use 24-hour format
          })}.pdf`}
          margin={{ bottom: 60 }}
          ref={resume}
        >
          <div className="text-left container pdf-container">
            <h1 className="text-center">
              <b>{getText('common:PAYMENT_RECEIPT')}</b>
            </h1>
            <div className="text-center">
              <p>
                {receiptObj?.texts?.header1?.length > 0 && (
                  <span>
                    {receiptObj.texts.header1}
                    <br />
                  </span>
                )}
                {receiptObj?.texts?.header2?.length > 0 && <span>{receiptObj.texts.header2}</span>}
              </p>
            </div>
            <p>{getText('common:PAGE_TITLE_TEXT')}</p>
            <p>
              <b>{getText('common:RECEIPT_NUMBER')}</b>
              <br />
              {receiptObj._id}
            </p>
            <p>
              <b>{getText('common:SALES_TRANSACTION')}</b>
              <br />
              {receiptObj.retailTransactionID}
            </p>
            <p>
              <b>{getText('common:DATE')}</b>
              <br />
              {receiptObj.currency === 'USD' ? moment.utc(receiptObj.receiptDate, true).tz('America/Los_Angeles').format('MM/DD/YYYY hh:mm a') : moment.utc(receiptObj.receiptDate, true).format('YYYY-MM-DD HH:mm')}
            </p>
            <p>
              <b>{getText('common:PAYMENT_STATUS')}</b>
              <br />
              Paid
            </p>
            <p>
              <b>{getText('common:SALES_CHANNEL')}</b>
              <br />
              {receiptObj?.salesChannel?.salesChannelName}
            </p>
            <br />
            <Table striped bordered hover responsive className="custom-table">
              <thead>
                <tr>
                  <th>{getText('common:PRODUCTS')}</th>
                  <th className="text-center">{getText('common:QUANTITY')}</th>
                  <th className="text-center">{getText('common:PRICE')}</th>
                  <th className="text-center">{getText('common:UNIT')}</th>
                  <th className="text-right">{getText('common:SUM')}</th>
                </tr>
              </thead>
              <tbody>
                {receiptObj?.lineItems?.map?.((item, idx) => {
                  return (
                    <tr key={idx}>
                      <td>{item.lineText}</td>
                      <td className="text-center">{item.quantity?.toString()?.length > 0 && item.quantity > 0 ? parseFloat(item.quantity).toFixed(4) : item.quantity}</td>
                      <td className="text-center">{item.pricePerUnit?.toString()?.length > 0 && item.pricePerUnit > 0 ? parseFloat(item.pricePerUnit).toFixed(2) : item.pricePerUnit}</td>
                      <td className="text-center">{item.unitCode?.length > 0 ? item.unitCode.substring(0, 5) : ''}</td>
                      <td className="text-right">
                        {item.amount} {receiptObj.currency}
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
                {receiptObj?.currency !== 'USD' &&
                  receiptObj?.vatSummary?.map?.((vat, idx) => {
                    return (
                      <tr key={idx}>
                        <td>VAT({vat.vatPercent}%)</td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td className="text-right">
                          {vat.vatAmount} {receiptObj.currency}
                        </td>
                      </tr>
                    );
                  })}
                <tr>
                  <td>
                    <b>{receiptObj?.currency === 'USD' ? getText('common:TOTAL_AMOUNT') : getText('common:TOTAL_AMOUNT_INCL_VAT')}</b>:
                  </td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td className="text-right">
                    <b>
                      {receiptObj.totalAmountInclVat} {receiptObj.currency}
                    </b>
                  </td>
                </tr>
                {receiptObj?.currency !== 'USD' && (
                  <tr>
                    <td>
                      <b>{getText('common:VAT_AMOUNT')}</b>:
                    </td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td className="text-right">
                      <b>
                        {receiptObj.totalVatAmount} {receiptObj.currency}
                      </b>
                    </td>
                  </tr>
                )}
                {receiptObj?.payments?.map?.((payment, idx) => {
                  return (
                    <tr key={idx}>
                      <td>{payment.pspType === PSP_TYPES.STRIPE ? CREDIT_CARD_TEXT : payment.pspType}</td>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                      <td className="text-right">
                        {payment.amount} {receiptObj.currency}
                      </td>
                    </tr>
                  );
                })}
                {receiptObj.customerInfoMasked && (
                  <tr>
                    <td className="pd-5">
                      {getText('common:CUSTOMER')}: {receiptObj.customerInfoMasked}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="pd-5">
                    {receiptObj.cardScheme && <span>{receiptObj.cardScheme}</span>}&nbsp;
                    {receiptObj.cardPan && <span>{receiptObj.cardPan}</span>}
                    <br />
                    {receiptObj.authorisationCode && (
                      <div>
                        {getText('common:AUTH_CODE')}: {receiptObj.authorisationCode}
                      </div>
                    )}
                    {receiptObj.emvAid && (
                      <div>
                        {getText('common:AID')}: {receiptObj.emvAid}
                      </div>
                    )}
                    {receiptObj.emvTvr && (
                      <div>
                        {getText('common:TVR')}: {receiptObj.emvTvr}
                      </div>
                    )}
                    {receiptObj.emvTsi && (
                      <div>
                        {getText('common:TSI')}: {receiptObj.emvTsi}
                      </div>
                    )}
                  </td>
                  <td className="pd-5" colSpan={4}>
                    &nbsp;
                  </td>
                </tr>
                {(receiptObj.usageTotalTimeMinutes || receiptObj?.usageTotalVolume?.toString?.().length > 0) && (
                  <tr>
                    <td colSpan={5}>
                      <b>{getText('common:ELECTRIC_VEHICLE_CHARGING')}</b>
                    </td>
                  </tr>
                )}
                {receiptObj.usageTotalTimeMinutes && receiptObj.usageTotalTimeMinutes > 0 && (
                  <tr>
                    <td>
                      <b>{getText('common:TIME')}:</b>
                    </td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td className="text-right pd-5">{`${minutes} min ${Math.round(seconds)} sec`}</td>
                  </tr>
                )}
                {receiptObj.usageTotalUnit && (
                  <tr>
                    <td colSpan="4">
                      <b>{getText('common:ENERGY_USED')}:</b>
                    </td>
                    <td className="text-right">
                      {receiptObj.usageTotalVolume ? (parseFloat(receiptObj.usageTotalVolume) / 1000).toFixed(4) : 0} {receiptObj.usageTotalUnit}
                    </td>
                  </tr>
                )}
                {receiptObj.componentName?.length > 0 && (
                  <tr>
                    <td colSpan="4" className="pd-5">
                      <b>{getText('common:CONNECTOR')}:</b>
                    </td>
                    <td className="text-right pd-5">{receiptObj.componentName}</td>
                  </tr>
                )}
                {receiptObj.connectorNumber?.length > 0 && (
                  <tr>
                    <td colSpan="4" className="pd-5">
                      <b>{getText('common:CONNECTOR_NUMBER')}:</b>
                    </td>
                    <td className="text-right pd-5">{receiptObj.connectorNumber}</td>
                  </tr>
                )}
                {receiptObj.posSerialNumber?.length > 0 && (
                  <tr>
                    <td colSpan="4" className="pd-5">
                      <b>{getText('common:CHARGER_SERIAL_NUMBER')}:</b>
                    </td>
                    <td className="text-right pd-5">{receiptObj.posSerialNumber}</td>
                  </tr>
                )}
                {receiptObj.detailedStopReason?.length > 0 ? (
                  <tr>
                    <td colSpan="4" className="pd-5">
                      <b>{getText('common:STOP_REASON')}:</b>
                    </td>
                    <td className="text-right pd-5">{receiptObj.detailedStopReason}</td>
                  </tr>
                ) : (
                  receiptObj.posSessionStopReason?.length > 0 && (
                    <tr>
                      <td colSpan="4" className="pd-5">
                        <b>{getText('common:STOP_REASON')}:</b>
                      </td>
                      <td className="text-right pd-5">{receiptObj.posSessionStopReason}</td>
                    </tr>
                  )
                )}
                {receiptObj.maxPower?.length > 0 && (
                  <tr>
                    <td colSpan="4" className="pd-5">
                      <b>{getText('common:MAX_POWER')}:</b>
                    </td>
                    <td className="text-right pd-5">{`${isNaN(receiptObj.maxPower) ? receiptObj.maxPower : parseFloat(receiptObj.maxPower).toFixed(4)} ${UNIT_KW}`}</td>
                  </tr>
                )}
                {receiptObj.detailedStopReason === 'POWER_LOSS' && (
                  <tr>
                    <td colSpan="5" className="pd-20">
                      <div>{`${getText('common:POWER_LOSS_TEXT')} ${receiptObj.currency === 'USD' ? `${moment.utc(receiptObj.usageStopDate, true).tz('America/Los_Angeles').format('MMM Do, YYYY')} at ${moment.utc(receiptObj.usageStopDate, true).tz('America/Los_Angeles').format('hh:mm:ss a')} time` : `${moment.utc(receiptObj.usageStopDate, true).format('MMM Do, YYYY')} at ${moment.utc(receiptObj.usageStopDate, true).format('hh:mm:ss a')} time`}`}</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <div className="d-flex flex-column customer-box">
              <div className="d-flex flex-row justify-content-between" style={{ flexBasis: '70%', gap: '5px' }}>
                <div>
                  <b>{getText('common:CUSTOMER')}:</b>
                </div>
                <div className="noExport">
                  {isEditCustomer ? (
                    <div className="d-flex flex-row" style={{ gap: '10px' }}>
                      <Button color="success" className="pull-right btn-labeled" size="sm" onClick={onSaveCustomer}>
                        <div className="btn-label">
                          <FontAwesomeIcon icon={faSave} size="xs" />
                        </div>
                        <span>{getText('common:SAVE')}</span>
                      </Button>
                      <Button color="warning" className="pull-right btn-labeled" size="sm" onClick={onSetEditCustomer}>
                        <div className="btn-label">
                          <FontAwesomeIcon icon={faCancel} size="xs" />
                        </div>
                        <span>{getText('common:CANCEL')}</span>
                      </Button>
                    </div>
                  ) : (
                    <Button color="success" className="pull-right btn-labeled" size="sm" onClick={onSetEditCustomer}>
                      <div className="btn-label">
                        <FontAwesomeIcon icon={faEdit} size="xs" />
                      </div>
                      <span>{getText('common:EDIT')}</span>
                    </Button>
                  )}
                </div>
              </div>
              <div className="d-flex flex-row flex-grow-1 align-items-baseline row">
                <div className="d-flex col-12 col-sm-4">{getText('common:CUSTOMER_NAME')}:</div>
                <div className="col-12 col-sm-3 justify-content-start pd-5">{isEditCustomer ? <Input value={fields.customerName} size="sm" name="customerName" onChange={onChange} placeholder={getText('common:CUSTOMER_NAME')} /> : receiptObj.customerName}</div>
              </div>
              <div className="d-flex flex-row flex-grow-1 align-items-baseline row">
                <div className="d-flex col-12 col-sm-4">{getText('common:CUSTOMER_MOBILE')}:</div>
                <div className="col-12 col-sm-3 justify-content-start pd-5">{isEditCustomer ? <ReactPhoneInput inputStyle={{ fontSize: '12px' }} onlyCountries={COUNTRIES_EXTN.map((country) => country.code)} value={fields?.customerMobilePhone?.length === 0 ? '+46' : fields.customerMobilePhone} countryCodeEditable={false} autoFormat={false} name="customerMobilePhone" preferredCountries={PREFERRED_COUNTRIES} onChange={(val) => onPhoneChange(val)} /> : receiptObj.customerMobilePhone}</div>
              </div>
              <div className="d-flex flex-row flex-grow-1 align-items-baseline row">
                <div className="d-flex col-12 col-sm-4">{getText('common:CUSTOMER_EMAIL')}:</div>
                <div className="col-12 col-sm-4 justify-content-start pd-5">
                  {isEditCustomer ? (
                    <span>
                      <Input value={fields.customerEmail} className={`${errors.customerEmail && `input-normal-error-msg`}`} size="sm" name="customerEmail" onChange={onChange} placeholder={getText('common:CUSTOMER_EMAIL')} />
                      <p className={`error-msg ${!errors.customerEmail && 'hidden'}`}>{errors.customerEmail}</p>
                    </span>
                  ) : (
                    receiptObj.customerEmail
                  )}
                </div>
              </div>
              <div className="d-flex flex-row flex-grow-1 align-items-baseline row">
                <div className="d-flex col-12 col-sm-4">{getText('common:CUSTOMER_VAT')}:</div>
                <div className="col-12 col-sm-3 justify-content-start pd-5">{isEditCustomer ? <Input value={fields.customerVatNumber} size="sm" name="customerVatNumber" onChange={onChange} placeholder={getText('common:CUSTOMER_VAT')} /> : receiptObj.customerVatNumber}</div>
              </div>
              <div className="d-flex flex-row flex-grow-1 align-items-baseline row">
                <div className="d-flex col-12 col-sm-4">{getText('common:CUSTOMER_TIN')}:</div>
                <div className="col-12 col-sm-3 justify-content-start pd-5">{isEditCustomer ? <Input value={fields.taxpayerIdentificationNumber} maxLength={20} size="sm" name="taxpayerIdentificationNumber" onChange={onChange} placeholder={getText('common:CUSTOMER_TIN')} /> : receiptObj.taxpayerIdentificationNumber}</div>
              </div>
            </div>
            <div className="noExport">
              {receiptObj.signedMeterData && !isEmpty(receiptObj.signedMeterData) && receiptObj.signedMeterData !== 'NoData' && (
                <div className="d-flex flex-column customer-box">
                  <div>{getText('common:CALIBRATION_DATA')}</div>
                  <div>
                    <a href="#" onClick={(ev) => onDownloadXMLFile(ev)}>
                      {getText('common:DOWNLOAD_CALIBRATION_DATA')}
                    </a>
                    <br />
                    <a href={DOWNLOAD_TRANSPARENCY_SOFTWARE_LINK} rel="noopener noreferrer" target="_blank">
                      {getText('common:DOWNLOAD_TRANSPARENCY_SOFTWARE_LINK_TEXT')}
                    </a>
                  </div>
                </div>
              )}
            </div>
            <div className="text-center short-box">
              <div className="mt-5">
                <b>{getText('common:SOLD_BY')}</b>
              </div>
              <hr className="mb-5 mt-0" />
              <p>
                {receiptObj.businessUnitName}
                {streetRow1?.length > 0 && (
                  <span>
                    <br />
                    {streetRow1}
                  </span>
                )}
                {streetRow2?.length > 0 && (
                  <span>
                    <br />
                    {streetRow2}
                  </span>
                )}
                {(zip?.length > 0 || city?.length > 0) && (
                  <span>
                    <br />
                    {zip} {city}
                  </span>
                )}
                {state?.length > 0 && (
                  <span>
                    <br />
                    {state}
                  </span>
                )}
                {country?.length > 0 && (
                  <span>
                    <br />
                    {country}
                  </span>
                )}
                {email?.length > 0 && (
                  <span>
                    <br />
                    {email}
                  </span>
                )}
                {phone?.length > 0 && (
                  <span>
                    <br />
                    {phone}
                  </span>
                )}
              </p>
              <p>
                {getText('common:VAT_NUMBER')} : {receiptObj.merchantVatNumber}
                <br />
                {getText('common:COMPANY_REGISTRATION')} : {receiptObj.merchantCompanyRegistrationNumber}
              </p>
              {receiptObj?.texts?.footer1?.length > 0 && <p>{receiptObj.texts.footer1}</p>}
              {receiptObj?.texts?.footer2?.length > 0 && <p>{receiptObj.texts.footer2}</p>}
            </div>
            {receiptObj.merchantID !== 'd0816285-1135-4a14-ab1d-7f5e4553c6bc' && (
              <div className="text-center">
                <QRCode value={`${params.baseUrl}receipt/${receiptid}`} size={160} level={'H'} />
              </div>
            )}
            <div className="text-center">
              <p>
                <b>{getText('common:PAYMENTS')}</b>
              </p>
              {receiptObj?.payments?.map?.((payment, idx) => {
                return (
                  <div key={idx}>
                    <p>{payment.pspType === PSP_TYPES.PSP_TYPE_STRIPE ? CREDIT_CARD_TEXT : payment.pspType}</p>
                    {payment.pspType === CREDIT_CARD_PAYMENT_TYPE && (
                      <p>
                        emvAid: {payment.transactionData.emvAid}
                        <br />
                        emvTsi: {payment.transactionData.emvTsi}
                        <br />
                        emvTvr: {payment.transactionData.emvTvr}
                        <br />
                        merchantId: {payment.transactionData.merchantId}
                        <br />
                        shortPaymentId: {payment.transactionData.shortPaymentId}
                        <br />
                        responseCode: {payment.transactionData.responseCode}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </PDFExport>
        <div className="container text-center">
          {receiptObj.rating && (
            <span className="pdf-export-rating">
              <Rating emptySymbol="fa fa-star-o" fullSymbol="fa fa-star gold" readonly initialRating={receiptObj.rating} />
            </span>
          )}
          <Button color="success" className="pdf-export-btn d-block" onClick={(ev) => onViewAsPDFClicked()}>
            {getText('common:VIEW_AS_PDF')}
          </Button>
        </div>
        <Footer pageLanguages={LANGUAGES} onLanguageChange={(opt) => onLanguageChange(opt)} defaultLanguage={language} footerBackgroundColor={''} />
      </section>
    </div>
  );
}

const mapStateToProps = (state) => selector(state, false, ['authorisation', 'app', 'receipt']);

const mapDispatchToProps = (dispatch) => {
  return {
    attemptGetReceipt: (data) => dispatch(attemptGetReceipt(data)),
    attemptUpdateReceipt: (data) => dispatch(attemptUpdateReceipt(data)),
    attemptSetLanguage: (data) => dispatch(attemptSetLanguage(data)),
    attemptResetReceiptNotificationState: (data) => dispatch(attemptResetReceiptNotificationState(data)),
  };
};

export default withTranslation('common')(connect(mapStateToProps, mapDispatchToProps)(Main));
